import { createRandomId } from "./createRandomId";

export type AuthEventType = "login" | "logout";

interface AuthEvent {
	type: AuthEventType;
	id: string;
	emittedAt: number;
}

const CHANNEL_NAME = "creatorverse-auth";
const STORAGE_FALLBACK_KEY = "creatorverse:auth-event";

let channel: BroadcastChannel | null | undefined;

const getChannel = (): BroadcastChannel | null => {
	if (channel !== undefined) return channel;
	if (typeof BroadcastChannel === "undefined") {
		channel = null;
		return channel;
	}

	try {
		channel = new BroadcastChannel(CHANNEL_NAME);
	} catch {
		channel = null;
	}
	return channel;
};

const isAuthEvent = (value: unknown): value is AuthEvent => {
	if (!value || typeof value !== "object") return false;
	const event = value as Partial<AuthEvent>;
	return (
		(event.type === "login" || event.type === "logout") &&
		typeof event.id === "string" &&
		typeof event.emittedAt === "number"
	);
};

const createEventId = (): string => createRandomId();

export const publishAuthEvent = (type: AuthEventType): void => {
	const event: AuthEvent = {
		type,
		id: createEventId(),
		emittedAt: Date.now(),
	};
	const authChannel = getChannel();

	if (authChannel) {
		authChannel.postMessage(event);
		return;
	}

	try {
		localStorage.setItem(STORAGE_FALLBACK_KEY, JSON.stringify(event));
	} catch {
		// Cross-tab synchronization is best-effort. Cookie-based auth
		// initialization still restores the session after a reload.
	}
};

export const subscribeToAuthEvents = (listener: (event: AuthEvent) => void): (() => void) => {
	const authChannel = getChannel();

	if (authChannel) {
		const handleMessage = (message: MessageEvent<unknown>) => {
			if (isAuthEvent(message.data)) listener(message.data);
		};
		authChannel.addEventListener("message", handleMessage);
		return () => authChannel.removeEventListener("message", handleMessage);
	}

	if (typeof window === "undefined") return () => {};

	const handleStorage = (storageEvent: StorageEvent) => {
		if (storageEvent.key !== STORAGE_FALLBACK_KEY || !storageEvent.newValue) return;
		try {
			const event: unknown = JSON.parse(storageEvent.newValue);
			if (isAuthEvent(event)) listener(event);
		} catch {
			// Ignore malformed fallback events.
		}
	};

	window.addEventListener("storage", handleStorage);
	return () => window.removeEventListener("storage", handleStorage);
};
